import { Button, Card, Space, Table, Typography, Input, Select, Col, Row, Tooltip, Tag, Tree } from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CategoryService } from '../../../Services/Manage/CategoryService';
import {
    EditOutlined, EyeOutlined, FolderOutlined, CheckCircleOutlined,
    StopOutlined, AppstoreOutlined, UploadOutlined, DownloadOutlined, PlusOutlined,
    FileTextOutlined, FilterOutlined, ExpandAltOutlined,
} from '@ant-design/icons';

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false, view_all: false };
    } catch { return { add: false, edit: false, delete: false, read: false, view_all: false }; }
};

const CATEGORY_PALETTE = ["#1677ff", "#52c41a", "#722ed1", "#fa8c16", "#13c2c2", "#eb2f96", "#f5222d"];

const StatCard = ({ title, value, sub, color, bg, icon }) => (
    <Card bodyStyle={{ padding: '10px 12px' }} style={{ borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: color, lineHeight: 1.2 }}>{value}</div>
                {sub && <div style={{ fontSize: 10, color: '#1677ff', marginTop: 3, cursor: 'pointer' }}>{sub}</div>}
            </div>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: color, flexShrink: 0 }}>
                {icon}
            </div>
        </div>
    </Card>
);

const quickLinks = [
    { label: "Add Category", to: "/inventory/categories/add-category" },
    { label: "Add Sub Category", to: "/inventory/categories/add-category" },
    { label: "Manage Categories", to: "/inventory/categories" },
    { label: "Category Reports", to: "/inventory/categories" },
];

const CategoriesDashboard = () => {
    const perm = getPermission('/inventory/categories');
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;

    const [allCategories, setAllCategories] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedParent, setSelectedParent] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [loading, setLoading] = useState(false);

    const getAllCategories = async () => {
        setLoading(true);
        try {
            const request = await CategoryService.getAllCategory();
            setAllCategories(request.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAllCategories();
    }, []);

    const visibleCategories = useMemo(
        () => perm.view_all ? allCategories : allCategories.filter((c) => String(c.added_by) === String(currentUserId)),
        [allCategories, perm.view_all, currentUserId]
    );

    const filteredCategories = useMemo(() => {
        let filtered = visibleCategories;

        if (searchText) {
            filtered = filtered.filter((c) =>
                Object.values(c).some((value) =>
                    value?.toString().toLowerCase().includes(searchText.toLowerCase())
                )
            );
        }

        if (selectedParent) {
            filtered = filtered.filter((c) => c.parent_category === selectedParent);
        }

        if (selectedStatus) {
            filtered = filtered.filter((c) => (selectedStatus === "Active" ? c.is_active : !c.is_active));
        }

        return filtered;
    }, [visibleCategories, searchText, selectedParent, selectedStatus]);

    const subCategoryCount = (categoryName) =>
        visibleCategories.filter((c) => c.parent_category === categoryName).length;

    // ---- Stats ----
    const totalCategories = visibleCategories.length;
    const activeCategories = useMemo(() => visibleCategories.filter((c) => c.is_active).length, [visibleCategories]);
    const inactiveCategories = totalCategories - activeCategories;
    const totalSubCategories = useMemo(() => visibleCategories.filter((c) => c.parent_category).length, [visibleCategories]);

    // ---- Category Tree ----
    const treeData = useMemo(() => {
        const topLevel = visibleCategories.filter((c) => !c.parent_category);
        return topLevel.map((parent) => {
            const children = visibleCategories.filter((c) => c.parent_category === parent.category_name);
            return {
                title: `${parent.category_name} (${children.length})`,
                key: `cat-${parent.id}`,
                icon: <FolderOutlined style={{ color: '#1677ff' }} />,
                children: children.map((child) => ({
                    title: child.category_name,
                    key: `cat-${child.id}`,
                    icon: <FolderOutlined style={{ color: '#bfbfbf' }} />,
                    isLeaf: true,
                })),
            };
        });
    }, [visibleCategories]);

    // ---- Top categories (by sub-category count) ----
    const topCategoriesData = useMemo(() => {
        const topLevel = visibleCategories.filter((c) => !c.parent_category);
        return topLevel
            .map((c, i) => ({ name: c.category_name, value: subCategoryCount(c.category_name), color: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length] }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 6);
    }, [visibleCategories]);
    const topCategoriesTotal = topCategoriesData.reduce((a, b) => a + b.value, 0);

    const parentOptions = [...new Set(visibleCategories.filter((c) => !c.parent_category).map((c) => c.category_name))].map((c) => ({ label: c, value: c }));
    const statusOptions = [{ label: "Active", value: "Active" }, { label: "Inactive", value: "Inactive" }];

    const columns = [
        { title: '#', dataIndex: 'key', width: '4%', align: 'center', key: 'key', render: (_, __, index) => index + 1 },
        {
            title: 'Category Name',
            dataIndex: 'category_name',
            key: 'category_name',
            render: (v) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13 }}>
                    <FolderOutlined style={{ color: '#1677ff' }} /> {v}
                </span>
            ),
        },
        { title: 'Parent Category', dataIndex: 'parent_category', key: 'parent_category', render: (v) => v || '—' },
        {
            title: 'Sub Categories',
            key: 'sub_categories',
            align: 'center',
            render: (_, record) => (record.parent_category ? '—' : subCategoryCount(record.category_name)),
        },
        { title: 'Description', dataIndex: 'description', key: 'description', render: (v) => v || '-' },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <Tag color={record.is_active ? 'success' : 'error'}>{record.is_active ? 'Active' : 'Inactive'}</Tag>
            ),
        },
        {
            title: 'Created On',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (v) => v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-',
        },
        {
            title: 'Action',
            key: 'Action',
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title={!perm.read ? "You don't have permission to view" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/categories/view-category', { state: { id: record.id, action: 'View', enableEdit: false } })}
                            icon={<EyeOutlined />} size="middle" disabled={!perm.read}
                        />
                    </Tooltip>
                    <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/categories/edit-category', { state: { id: record.id, action: 'Edit', enableEdit: true } })}
                            icon={<EditOutlined />} size="middle" disabled={!perm.edit}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>Categories</Typography.Title>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dashboard &gt; Inventory Management &gt; Categories</Typography.Text>
                </div>
                <Space wrap>
                    <Button size="middle" icon={<UploadOutlined />}>Import Categories</Button>
                    <Button size="middle" icon={<DownloadOutlined />}>Export</Button>
                    <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
                        <Button
                            size="middle" type="primary" disabled={!perm.add}
                            icon={<PlusOutlined />}
                            onClick={() => navigate('/inventory/categories/add-category', { state: { action: 'Add', enableEdit: true } })}
                            style={{ background: '#1677ff', borderColor: '#1677ff' }}
                        >
                            Add Category
                        </Button>
                    </Tooltip>
                </Space>
            </div>

            <div style={{ padding: '8px 16px 0' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} sm={12} md={6}>
                        <StatCard title="Total Categories" value={totalCategories} sub="View all categories" color="#1677ff" bg="#f0f7ff" icon={<AppstoreOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <StatCard title="Active Categories" value={activeCategories} sub="View active categories" color="#52c41a" bg="#f6ffed" icon={<CheckCircleOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <StatCard title="Inactive Categories" value={inactiveCategories} sub="View inactive categories" color="#fa8c16" bg="#fff7e6" icon={<StopOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <StatCard title="Total Sub Categories" value={totalSubCategories} sub="View all sub categories" color="#1658b4" bg="#eef4ff" icon={<AppstoreOutlined />} />
                    </Col>
                </Row>
            </div>

            <div style={{ padding: '6px 16px 16px' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} lg={17}>
                        <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Row gutter={[16, 16]} align="bottom">
                                <Col xs={24} sm={12} md={8}>
                                    <Input
                                        allowClear
                                        placeholder="Search category name..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </Col>
                                <Col xs={24} sm={12} md={5}>
                                    <Select allowClear placeholder="All Status" options={statusOptions}
                                        value={selectedStatus} onChange={setSelectedStatus} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Select allowClear placeholder="All Parent Categories" options={parentOptions}
                                        value={selectedParent} onChange={setSelectedParent} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Button icon={<FilterOutlined />} style={{ width: "100%" }}>More Filters</Button>
                                </Col>
                                <Col xs={24} sm={12} md={1}>
                                    <Button icon={<DownloadOutlined />} style={{ width: "100%" }} />
                                </Col>
                            </Row>
                        </Card>

                        <Table
                            loading={loading}
                            scroll={{ x: 'max-content' }}
                            rowKey="id"
                            dataSource={filteredCategories.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at))}
                            columns={columns}
                        />
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card
                            style={{ borderRadius: 10, marginBottom: 16 }}
                            bodyStyle={{ padding: 12 }}
                            title={<Typography.Text strong>Category Tree</Typography.Text>}
                            extra={<ExpandAltOutlined style={{ color: '#888' }} />}
                            size="small"
                        >
                            {treeData.length === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>📁</span>
                                    <span style={{ marginTop: 6 }}>No data yet</span>
                                </div>
                            ) : (
                                <Tree
                                    showIcon
                                    treeData={treeData}
                                />
                            )}
                            <div style={{ marginTop: 8 }}>
                                <a onClick={() => navigate('/inventory/categories')} style={{ fontSize: 12 }}>View Full Tree</a>
                            </div>
                        </Card>

                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}
                            title={<Typography.Text strong>Top Categories</Typography.Text>}
                            extra={<a onClick={() => navigate('/inventory/categories')} style={{ fontSize: 12 }}>View All</a>}
                            size="small"
                        >
                            {topCategoriesTotal === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>🗂️</span>
                                    <span style={{ marginTop: 6 }}>No data yet</span>
                                </div>
                            ) : (
                                <div>
                                    {topCategoriesData.map((c, i) => (
                                        <div key={i} style={{ marginBottom: 10 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                                <span style={{ color: '#333' }}>{c.name}</span>
                                                <span style={{ fontWeight: 700, color: '#333' }}>{c.value} Sub</span>
                                            </div>
                                            <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3 }}>
                                                <div style={{ width: `${topCategoriesTotal ? Math.round((c.value / topCategoriesTotal) * 100) : 0}%`, height: '100%', background: c.color, borderRadius: 3 }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><FileTextOutlined /> Quick Links</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {quickLinks.map(({ label, to }) => (
                                    <div
                                        key={label}
                                        onClick={() => navigate(to)}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontSize: 13, color: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        <span>{label}</span>
                                        <span>›</span>
                                    </div>
                                ))}
                            </Space>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

export default CategoriesDashboard;